import { getAuthToken, setAuthToken, clearAuthToken } from '../utils/authStorage';

import API_BASE_URL from '../config/apiBaseUrl';

class FriendsService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = getAuthToken();
  }

  getHeaders(contentType = 'application/json') {
    const freshToken = getAuthToken();
    if (freshToken) {
      this.token = freshToken;
    }

    const headers = {
      'Content-Type': contentType,
      'Accept': 'application/json',
    };
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }
    return headers;
  }

  async handleResponse(response) {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      let errorMessage = errorData.detail || errorData.message || errorData.title || 'Erreur inconnue';

      if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
        errorMessage = errorData.errors[0].message || errorMessage;
      }

      throw new Error(errorMessage);
    }
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return null;
    }
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }
    return null;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const mergedHeaders = {
      ...this.getHeaders(),
      ...(options.headers || {})
    };
    const config = {
      ...options,
      headers: mergedHeaders,
    };
    try {
      const response = await fetch(url, config);
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Erreur requête API Friends:', error);
      throw error;
    }
  }

  async requestByUrl(url, options = {}) {
    const fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`;
    const mergedHeaders = {
      ...this.getHeaders(),
      ...(options.headers || {})
    };
    const config = {
      ...options,
      headers: mergedHeaders,
    };
    try {
      const response = await fetch(fullUrl, config);
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Erreur requête API:', error);
      throw error;
    }
  }

  // ===== FRIENDS =====

  async getFriends() {
    try {
      const response = await this.request('/friends');
      if (Array.isArray(response)) {
        return response;
      } else if (response['hydra:member']) {
        return response['hydra:member'];
      }
      return [];
    } catch (error) {
      console.error('Erreur chargement amis:', error);
      throw error;
    }
  }

  async getFriendProfile(username) {
    return await this.request(`/friends/${encodeURIComponent(username)}/profile`);
  }

  async sendFriendRequest(targetUserId, currentUserId) {
    return await this.request('/friends', {
      method: 'POST',
      body: JSON.stringify({
        sender: `/api/users/${currentUserId}`,
        receiver: `/api/users/${targetUserId}`,
        status: 'pending'
      })
    });
  }

  async acceptFriendRequest(friendId) {
    return await this.request(`/friends/${friendId}`, {
      method: 'PATCH',
      headers: this.getHeaders('application/merge-patch+json'),
      body: JSON.stringify({ status: 'accepted' })
    });
  }

  async rejectFriendRequest(friendId) {
    return await this.request(`/friends/${friendId}`, {
      method: 'PATCH',
      headers: this.getHeaders('application/merge-patch+json'),
      body: JSON.stringify({ status: 'rejected' })
    });
  }

  async removeFriend(friendId) {
    return await this.request(`/friends/${friendId}`, {
      method: 'DELETE'
    });
  }

  // ===== SEARCH USERS =====

  async searchUsers(query) {
    try {
      const response = await this.request('/journals/public');
      let journals = [];
      if (Array.isArray(response)) {
        journals = response;
      } else if (response['hydra:member']) {
        journals = response['hydra:member'];
      }

      const searchLower = query.toLowerCase().trim();

      const filteredUsers = journals
        .filter(journal => {
          const user = journal.user;
          if (!user) return false;
          const username = (user.username || '').toLowerCase();
          const firstname = (user.firstname || '').toLowerCase();
          const lastname = (user.lastname || '').toLowerCase();
          return username.includes(searchLower) || firstname.includes(searchLower) || lastname.includes(searchLower);
        })
        .map(journal => {
          const user = journal.user;
          return {
            id: user.id,
            username: user.username,
            firstname: user.firstname,
            lastname: user.lastname,
            avatar: user.avatar || null,
            journalId: journal.id
          };
        });

      return filteredUsers;
    } catch (error) {
      console.error('Erreur recherche utilisateurs:', error);
      throw error;
    }
  }

  // ===== JOURNAL AMI =====

  async getFriendJournalByUsername(username) {
    try {
      const response = await this.request(`/friends/${encodeURIComponent(username)}/journal`);
      return response;
    } catch (error) {
      if (error.message && error.message.includes('Cannot unset string offsets')) {
        console.error('Erreur de sérialisation côté serveur');
      }
      throw error;
    }
  }

  async getMealByUrl(mealUrl) {
    try {
      return await this.requestByUrl(mealUrl);
    } catch (error) {
      console.error('Erreur chargement repas:', error);
      throw error;
    }
  }

  async getJournalMeals(journalUrl) {
    try {
      const journal = await this.getJournalByUrl(journalUrl);

      if (!journal || !journal.meals) {
        return [];
      }

      const meals = await Promise.all(
        journal.meals.map(async (meal) => {
          if (typeof meal === 'string') {
            return await this.getMealByUrl(meal);
          }
          return meal;
        })
      );

      return meals;
    } catch (error) {
      console.error('Erreur chargement repas du journal:', error);
      throw error;
    }
  }

  // ===== AUTH TOKEN =====

  updateAuthToken(newToken) {
    this.token = newToken;
    setAuthToken(newToken);
  }

  clearAuthToken() {
    this.token = null;
    clearAuthToken();
  }

  // ===== BLOCKED USERS =====

  async getBlockedUsers() {
    try {
      const response = await this.request('/blocked-users');
      if (Array.isArray(response)) {
        return response;
      } else if (response['hydra:member']) {
        return response['hydra:member'];
      }
      return [];
    } catch (error) {
      console.error('Erreur chargement utilisateurs bloqués:', error);
      throw error;
    }
  }

  async blockUser(userId) {
    return await this.request('/blocked-users', {
      method: 'POST',
      body: JSON.stringify({
        blockedUser: `/api/users/${userId}`
      })
    });
  }

  async unblockUser(blockedUserId) {
    return await this.request(`/blocked-users/${blockedUserId}`, {
      method: 'DELETE'
    });
  }

  // ===== TRACKING DATA AMI =====

  async getFriendWeightData(username) {
    try {
      const response = await this.request(`/friends/${encodeURIComponent(username)}/weight`);
      return response;
    } catch (error) {
      console.error('Erreur chargement données poids ami:', error);
      throw error;
    }
  }

  async getFriendSleepData(username) {
    try {
      const response = await this.request(`/friends/${encodeURIComponent(username)}/sleep`);

      return response;
    } catch (error) {
      console.error('Erreur chargement données sommeil ami:', error);
      throw error;
    }
  }

}

const friendsService = new FriendsService();

export default friendsService;
